import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
} from '@heidi/design-system/avatar';
import { cn } from '@heidi/utils';
import { FileText } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import { useEffect, useState, type JSX } from 'react';

import {
  LOGO_WINDOW,
  publishersOf,
  sourceLogo,
  type EvidenceSource,
  type Publisher,
} from './evidenceTrail';

/** px; the strip's width is arithmetic on these. */
const LOGO_PX = 16;
const GAP_PX = 4;
const STEP_PX = LOGO_PX + GAP_PX;

/** How long a logo stays put before the strip moves on. */
const HOLD_MS = 1200;
/** The shift itself: the slow end of the house range, since it repeats. */
const SHIFT_MS = 300;
/** One `STEP_PX` toward the start edge, whichever side that is. */
const SHIFT_CLASS = '-translate-x-5 rtl:translate-x-5';

const windowFrom = (
  pool: readonly Publisher[],
  offset: number,
  count: number
): readonly Publisher[] =>
  Array.from({ length: count }, (_, index) => pool[(offset + index) % pool.length]!);

const LogoStrip = ({ pool }: { pool: readonly Publisher[] }): JSX.Element => {
  const reduceMotion = useReducedMotion();
  const visible = Math.min(LOGO_WINDOW, pool.length);
  // Rotation reveals what does not fit; with everything on screen it would only
  // permute the same logos.
  const drifts = pool.length > visible && !reduceMotion;
  const [offset, setOffset] = useState(0);
  const [shifting, setShifting] = useState(false);

  const logos = pool.map((publisher) => publisher.logo).join('\n');
  useEffect(() => {
    for (const logo of logos.split('\n')) new Image().src = logo;
  }, [logos]);

  useEffect(() => {
    if (!drifts) return;
    let timer: ReturnType<typeof setTimeout>;
    const hold = () => {
      timer = setTimeout(() => {
        setShifting(true);
        timer = setTimeout(() => {
          setShifting(false);
          setOffset((current) => current + 1);
          hold();
        }, SHIFT_MS);
      }, HOLD_MS);
    };
    hold();
    return () => {
      clearTimeout(timer);
      setShifting(false);
    };
  }, [drifts]);

  const strip = windowFrom(pool, offset, drifts ? visible + 1 : visible);
  return (
    <span
      aria-hidden
      data-testid="evidence-source-cycle"
      className="ms-1 inline-flex shrink-0 overflow-hidden transition-[width] ease-out-cubic motion-reduce:transition-none"
      style={{ width: visible * STEP_PX - GAP_PX, transitionDuration: `${SHIFT_MS}ms` }}
    >
      <span
        data-testid="evidence-source-strip"
        className={cn(
          'flex shrink-0 gap-1 ease-out-cubic',
          // Only the shift animates; the commit that advances the window drops
          // the transform with no transition, so the drift never snaps back.
          shifting ? cn('transition-transform', SHIFT_CLASS) : 'transition-none'
        )}
        style={{ transitionDuration: `${SHIFT_MS}ms` }}
      >
        {strip.map((publisher, index) => (
          // eslint-disable-next-line @next/next/no-img-element -- third-party favicons off arbitrary domains; next/image would need each host allow-listed
          <img
            // Keyed by publisher: a logo that stays in view keeps its node, and
            // one entering or leaving gets its own, so a fade never carries
            // across a step.
            key={publisher.domain}
            src={publisher.logo}
            alt=""
            className={cn(
              'size-4 shrink-0 rounded-sm bg-background-secondary transition-opacity ease-out-cubic',
              drifts && index === 0 && shifting && 'opacity-0',
              drifts && index === strip.length - 1 && !shifting && 'opacity-0'
            )}
            style={{ transitionDuration: `${SHIFT_MS}ms` }}
            onError={(event) => {
              event.currentTarget.style.visibility = 'hidden';
            }}
          />
        ))}
      </span>
    </span>
  );
};

/**
 * The publishers a running Evidence step has found so far: a short strip of
 * logos that drifts one along when there are more than fit, and holds still
 * under reduced motion. Texture, not a result, so it never counts.
 */
export const SourceCycle = ({
  sources,
}: {
  sources: readonly EvidenceSource[];
}): JSX.Element | null => {
  const pool = publishersOf(sources);
  const head = pool[0];
  // A new head is a new ranking, which starts again from its most authoritative
  // publisher.
  return head ? <LogoStrip key={head.domain} pool={pool} /> : null;
};

/**
 * The same publishers held still once the step is done: the top three,
 * overlapped. No `+N`; the count beside it already says how many.
 */
export const SourceAvatars = ({
  sources,
}: {
  sources: readonly EvidenceSource[];
}): JSX.Element | null => {
  const top = publishersOf(sources).slice(0, LOGO_WINDOW);
  if (top.length === 0) return null;
  return (
    // Decorative: the count beside it says what they stand for.
    <AvatarGroup aria-hidden className="shrink-0" data-testid="evidence-source-avatars">
      {top.map((publisher) => (
        <Avatar key={publisher.domain} size="xs" className="bg-surface">
          <AvatarImage src={publisher.logo} alt="" />
          <AvatarFallback className="text-text-tertiary">
            {publisher.domain.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ))}
    </AvatarGroup>
  );
};

/** One source's publisher logo in the opened list; a document glyph when it has none. */
export const SourceGlyph = ({ source }: { source: EvidenceSource }): JSX.Element => {
  const [failed, setFailed] = useState(false);
  const logo = sourceLogo(source);
  if (failed || !logo) {
    return (
      <FileText
        className="size-3.5 shrink-0 text-text-tertiary"
        aria-hidden
        data-testid="evidence-source-glyph-fallback"
      />
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element -- third-party favicons off arbitrary domains; next/image would need each host allow-listed
    <img
      src={logo}
      alt=""
      className="size-3.5 shrink-0 rounded-sm bg-background-secondary"
      onError={() => setFailed(true)}
    />
  );
};
